import { useEffect, useRef, useState } from 'react';
import type { WorkbookDefinition, WorkbookResult } from '@erp/shared-types';
import { rpcClient } from '@/api/rpc/client';

/** How long edits must pause before the Live preview recomputes. */
export const PREVIEW_DEBOUNCE_MS = 600;

export type FreshSource = { id: string; name: string; readAt: string };

export type WorkbookPreview = {
  /** The newest answered Live preview's Results: the target Worksheet's and everything downstream. */
  results: readonly WorkbookResult[];
  /** When the Base run's inputs were read, for the Sources the preview reused. */
  inputsAsOf: string | null;
  /** The Sources the preview read fresh, and when, rather than reusing the Base run's. */
  freshSources: readonly FreshSource[];
  reusedSourceCount: number;
  /** A first look, built on no Run: how many of each Source's first rows it read. */
  headRows: number | null;
  pending: boolean;
  error: string | null;
};

type PreviewInputs = {
  workbookId: string;
  definition: WorkbookDefinition;
  sheetId: string | null;
  baseRunId: string | null;
};
/** The newest answered preview and the inputs it answered. */
type Answer = Omit<WorkbookPreview, 'pending'> & {
  inputs: PreviewInputs | null;
};

const NO_ANSWER: Answer = {
  inputs: null,
  results: [],
  inputsAsOf: null,
  freshSources: [],
  reusedSourceCount: 0,
  headRows: null,
  error: null,
};

function samePreviewInputs(a: PreviewInputs | null, b: PreviewInputs) {
  return (
    a !== null &&
    a.workbookId === b.workbookId &&
    a.definition === b.definition &&
    a.sheetId === b.sheetId &&
    a.baseRunId === b.baseRunId
  );
}

/**
 * Live preview: "Run from this sheet" computed on the server for the edited Definition,
 * debounced, while `enabled`; with no Base run, a first look over each Source's first rows. Only the newest request's answer is kept; superseded
 * answers are dropped. Results carry the Fingerprint they were computed for, so a caller
 * shows one only while it matches the edited Definition.
 */
export function useWorkbookPreview({
  workbookId,
  definition,
  sheetId,
  baseRunId,
  enabled,
}: {
  workbookId: string;
  definition: WorkbookDefinition;
  sheetId: string | null;
  /** A new Base run recomputes the Live preview. */
  baseRunId: string | null;
  enabled: boolean;
}): WorkbookPreview {
  // Pending is derived: the newest answer is for other inputs than these.
  const inputs = { workbookId, definition, sheetId, baseRunId };
  const [answer, setAnswer] = useState<Answer>(NO_ANSWER);
  const latest = useRef(0);
  useEffect(() => {
    const request = ++latest.current;
    if (!enabled || !sheetId) return;
    const answered = { workbookId, definition, sheetId, baseRunId };
    const timer = window.setTimeout(() => {
      const input = { workbookId, definition, targetSheetId: sheetId };
      // No Base run to build on: a first look over each Source's first rows.
      const asked: Promise<Omit<Answer, 'inputs' | 'error'>> =
        baseRunId === null
          ? rpcClient.workbooks.firstLook(input).then((response) => ({
              results: response.results,
              inputsAsOf: null,
              freshSources: response.freshSources,
              reusedSourceCount: 0,
              headRows: response.headRows,
            }))
          : rpcClient.workbooks.preview(input).then((response) => ({
              results: response.results,
              inputsAsOf: response.inputsAsOf,
              freshSources: response.freshSources,
              reusedSourceCount: response.reusedSourceCount,
              headRows: null,
            }));
      asked.then(
        (response) => {
          if (request !== latest.current) return;
          setAnswer({ inputs: answered, ...response, error: null });
        },
        (cause: unknown) => {
          if (request !== latest.current) return;
          setAnswer((current) => ({
            ...current,
            inputs: answered,
            error: cause instanceof Error ? cause.message : String(cause),
          }));
        },
      );
    }, PREVIEW_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [workbookId, definition, sheetId, baseRunId, enabled]);
  const { inputs: answered, ...preview } = answer;
  return {
    ...preview,
    pending: enabled && !!sheetId && !samePreviewInputs(answered, inputs),
  };
}
